import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { Observable } from "rxjs";
import { AuthService } from "@auth0/auth0-angular";
import {
  isPermissionsFLag as permissfig,
  userInfo as userFlag,
} from "../guards/permissions";
import { CommonService } from "../services/common.service";

import { environment as env } from "../../../environments/environment";
import { NzModalService } from "ng-zorro-antd/modal";
import { TranslateService } from "@ngx-translate/core";
import { AppUserLoginComponent } from "../components/app-user-login/app-user-login.component";

import jwtDecode from "jwt-decode";

@Injectable({
  providedIn: "root",
})
export class LoginGuard implements CanActivate {
  private readonly PERMISSION_MAP = {
    glucose: {
      permission: "associationrole:glucose",
      url: "/blood",
    },
    medicalreport: {
      permission: "associationrole:medicalreport",
      url: "/medicalreport",
    },
    foods: {
      permission: "associationrole:foods",
      url: "/food",
    },
    foodsunit: {
      permission: "associationrole:foodsunit",
      url: "/food/unit",
    },
    drugs: {
      permission: "associationrole:drugs",
      url: "/drug",
    },
    drugsunit: {
      permission: "associationrole:drugsunit",
      url: "/drug/unit",
    },
    sports: {
      permission: "associationrole:sports",
      url: "/sport",
    },
    sportsunit: {
      permission: "associationrole:sportsunit",
      url: "/sport/unit",
    },
    updatepassword: {
      permission: "associationrole:updatepassword",
      url: "/change-passwd",
    },
    usermanagement: {
      permission: "associationrole:usermanagement",
      url: "/users",
    },
    rolessetting: {
      permission: "associationrole:rolessetting",
      url: "/user",
    },
    userrightsmanagement: {
      permission: "associationrole:userrightsmanagement",
      url: "/authority",
    },
    departmentmanagement: {
      permission: "associationrole:departmentmanagement",
      url: "/department",
    },
    organizationmanagement: {
      permission: "associationrole:organizationmanagement",
      url: "/organization",
    },
  };

  private userInfo: any;

  constructor(
    private router: Router,
    private auth: AuthService,
    private common: CommonService,
    private modalService: NzModalService,
    private translate: TranslateService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    return new Observable<boolean>((observer) => {
      const auth0Login = (connection?: string) => {
        observer.next(false);
        observer.complete();

        if (!!connection && connection.trim().length) {
          this.auth.loginWithRedirect({ connection });
        } else {
          this.auth.loginWithRedirect({ connection: env.cmsUserConnection });
        }
      };

      const doLogin = () => {

        console.log("isAppUserCms = " + env.isAppUserCms);

        if (!env.isAppUserCms) {
          auth0Login();
        } else {
          const modalRef = this.modalService.create({
            // nzTitle: this.translate.instant("appUserLogin.title"),
            nzContent: AppUserLoginComponent,
            nzFooter: null,
            nzClosable: false,
            nzMaskClosable: false,
            nzBodyStyle: {
              "background-color": "#23262d",
              "border-radius": "2px",
            },
          });

          modalRef.afterClose.subscribe((connection: string) => {
            auth0Login(connection);
          });
        }
      };

      this.auth.user$.subscribe(
        async (user) => {
          if (!user) {
            doLogin();
          } else {
            const userInfo = await this.getAndDecodeAccessToken();
            const permissions = userInfo.permissions || [];

            this.userInfo = userInfo;
            userFlag.userData = userInfo;

            this.checkPermissions(permissions);
            this.setAdminPermissions(userInfo.user_metadata);

            if (state.url === "/home" || this.checkAccessableUrl(state.url)) {
              observer.next(true);
              observer.complete();
            } else {
              observer.next(false);
              observer.complete();
              this.router.navigate(["/home"]);
            }
          }
        },
        (error) => {
          doLogin();
        }
      );
    });
  }

  getAndDecodeAccessToken() {
    return new Promise<any>((resolve, reject) => {
      this.auth.getAccessTokenSilently().subscribe(
        (token) => {
          let aud = env.auth0.audience;
          let audUserMetadataKey = aud.endsWith("/")
            ? aud + "user_metadata"
            : aud + "/user_metadata";

          const json = jwtDecode(token);
          const user_metadata = json[audUserMetadataKey];

          json["user_metadata"] = user_metadata;
          delete json[audUserMetadataKey];

          resolve(json);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  checkPermissions(permissions: string[]) {
    const keys: string[] = Object.keys(this.PERMISSION_MAP);

    if (!permissions || !permissions.length) {
      keys.forEach((key) => {
        permissfig[key] = false;
      });

      if (env.isAppUserCms) {
        permissfig.glucose = true;
        permissfig.medicalreport = true;
      }
    } else {
      keys.forEach((key) => {
        if (permissions.includes(this.PERMISSION_MAP[key]["permission"])) {
          permissfig[key] = true;
        } else {
          permissfig[key] = false;
        }
      });
    }
  }

  setAdminPermissions(userMetadata: any) {

    // userMetadata.administrator_rights = 'organization_system';

    if (!userMetadata) {
      return;
    }

    if (userMetadata.administrator_rights == "global_system") {
      //全局系统管理员所包含的权限
      permissfig.globalsystem = true;
      permissfig.foods = true;
      permissfig.foodsunit = true;
      permissfig.drugs = true;
      permissfig.drugsunit = true;
      permissfig.sports = true;
      permissfig.sportsunit = true;
      permissfig.rolessetting = true;
      permissfig.userrightsmanagement = true;
      permissfig.organizationmanagement = true;
    } else if (userMetadata.administrator_rights == "organization_system") {
      //判断为组织管理员登录
      permissfig.organizationsystem = true;
      permissfig.glucose = true;
      permissfig.medicalreport = true;
      permissfig.departmentmanagement = true;
      permissfig.userrightsmanagement = true;
    }
  }

  checkAccessableUrl(url: string) {
    const keys: string[] = Object.keys(this.PERMISSION_MAP);

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];

      if (this.PERMISSION_MAP[key]["url"] === url) {
        return permissfig[key];
      }
    }

    return false;
  }
}
